import { Vector3 } from 'three'
import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { ILine, IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { BaseEntity } from './BaseEntity'

const EPSILON = 1e-6
const PI2 = Math.PI * 2

/** 角度标注计算数据 */
export interface IAngularDimensionData {
  /** 两条标注线延长后的交点 */
  intersection: Vector3
  /** 起始边的单位方向向量 */
  startDirection: Vector3
  /** 终止边的单位方向向量 */
  endDirection: Vector3
  /** 弧的起点，位于起始边上 */
  arcStart: Vector3
  /** 弧的终点，位于终止边上 */
  arcEnd: Vector3
  /** 弧半径，单位米 */
  radius: number
  /** 弧起始角度，单位弧度 */
  startAngle: number
  /** 弧终止角度，单位弧度 */
  endAngle: number
  /** 弧方向是否为顺时针 */
  clockwise: boolean
  /** 弧扫掠角度，单位弧度 */
  sweepAngle: number
  /** 弧长，单位米 */
  arcLength: number
  /** 文字位置 */
  textPosition: Vector3
  /** 角度值，单位度 */
  angleDegrees: number
}

/** 角度标注两侧定义端点 */
export interface IAngularDimensionDefinitionPoints {
  /** 起始边定义端点 */
  firstPoint: IPoint
  /** 终止边定义端点 */
  secondPoint: IPoint
}

/** 角度标注实体 */
export class AngularDimensionEntity extends BaseEntity {
  /** 第一条标注边 */
  line1!: ILine
  /** 第二条标注边 */
  line2!: ILine
  /** 决定标注弧位置的参考点 */
  arcPoint!: IPoint
  /** 标注文字内容，为空时使用自动计算的角度值 */
  dimensionText: string = ''
  /** 标注文字位置 */
  textPosition!: IPoint
  /** 标注文字位置是否被手动编辑过 */
  isTextPositionEdited: boolean = false
  /** 标注文字高度，单位毫米 */
  textHeight: number = 2.5

  protected readonly serializeKeys: string[] = [
    'line1',
    'line2',
    'arcPoint',
    'dimensionText',
    'textPosition',
    'isTextPositionEdited',
    'textHeight',
  ]

  constructor(data: Partial<AngularDimensionEntity>) {
    super(EntityType.AngularDimension)
    Object.assign(this, data)
  }

  /**
   * 获取捕捉数据，返回弧端点、定义端点与延长线交点。
   *
   * @returns 包含端点、圆心点与延长线的捕捉数据；无法计算时返回空对象
   */
  getCaptureData() {
    const dimensionData = getAngularDimensionData(
      this.line1,
      this.line2,
      this.arcPoint,
      this.textPosition
    )
    if (!dimensionData) {
      return {}
    }

    const intersection = toPoint(dimensionData.intersection)
    const arcStart = toPoint(dimensionData.arcStart)
    const arcEnd = toPoint(dimensionData.arcEnd)
    const { firstPoint, secondPoint } = getAngularDimensionDefinitionPoints(this, dimensionData)
    const legLines = getAngularDimensionLegLines(this, dimensionData)

    return {
      endPoints: [arcStart, arcEnd, firstPoint, secondPoint],
      centerPoints: [intersection],
      lines: legLines,
    }
  }
}

/**
 * 同步角度标注文本位置到 arcPoint；若文本已被手动编辑则保持不变。
 *
 * @param entity - 角度标注实体的部分字段
 */
export function syncAngularDimensionTextPosition(
  entity: Pick<AngularDimensionEntity, 'arcPoint' | 'textPosition' | 'isTextPositionEdited'>
) {
  if (entity.isTextPositionEdited || !entity.arcPoint) {
    return
  }

  entity.textPosition = {
    x: entity.arcPoint.x,
    y: entity.arcPoint.y,
  }
}

/**
 * 获取角度标注的两侧定义端点，用于 AutoCAD grip 与端点显示。
 *
 * @param entity - 角度标注实体或包含两条标注边的对象
 * @param dimensionData - 已计算出的角度标注几何数据
 * @returns 起始边与终止边上的定义端点
 */
export function getAngularDimensionDefinitionPoints(
  entity: Pick<AngularDimensionEntity, 'line1' | 'line2'>,
  dimensionData: IAngularDimensionData
): IAngularDimensionDefinitionPoints {
  const lines = [entity.line1, entity.line2] as const
  const startLineIndex = findAngularDimensionLineIndex(lines, dimensionData.startDirection)
  const endLineIndex = findAngularDimensionLineIndex(
    lines,
    dimensionData.endDirection,
    startLineIndex
  )

  return {
    firstPoint: getAngularDimensionLegPoint(
      lines[startLineIndex],
      dimensionData.intersection,
      dimensionData.startDirection,
      dimensionData.arcStart
    ),
    secondPoint: getAngularDimensionLegPoint(
      lines[endLineIndex],
      dimensionData.intersection,
      dimensionData.endDirection,
      dimensionData.arcEnd
    ),
  }
}

/**
 * 获取角度标注两侧需要补画的显示线段。
 * 当弧端点已经落在被选线段内时，AutoCAD 不再额外绘制重叠腿线。
 *
 * @param entity - 角度标注实体或包含两条标注边的对象
 * @param dimensionData - 已计算出的角度标注几何数据
 * @returns 起始边与终止边上需要补画的显示线段
 */
export function getAngularDimensionLegLines(
  entity: Pick<AngularDimensionEntity, 'line1' | 'line2'>,
  dimensionData: IAngularDimensionData
): ILine[] {
  const lines = [entity.line1, entity.line2] as const
  const startLineIndex = findAngularDimensionLineIndex(lines, dimensionData.startDirection)
  const endLineIndex = findAngularDimensionLineIndex(
    lines,
    dimensionData.endDirection,
    startLineIndex
  )
  const legLines = [
    getAngularDimensionVisibleLegLine(
      lines[startLineIndex],
      dimensionData.intersection,
      dimensionData.startDirection,
      dimensionData.arcStart
    ),
    getAngularDimensionVisibleLegLine(
      lines[endLineIndex],
      dimensionData.intersection,
      dimensionData.endDirection,
      dimensionData.arcEnd
    ),
  ]

  return legLines.filter((line): line is ILine => !!line)
}

/**
 * 计算角度标注的几何数据，给出标注弧起止点、方向与角度。
 *
 * @param line1 - 第一条标注边
 * @param line2 - 第二条标注边
 * @param arcPoint - 弧所在位置参考点，用于决定四个角中哪一个被标注
 * @param textPosition - 文字位置，默认与 arcPoint 一致
 * @param minRadius - 计算弧时使用的最小半径，单位米，避免零半径
 * @returns 标注几何数据；两线平行、交点无法确定或方向无效时返回 null
 */
export function getAngularDimensionData(
  line1: ILine,
  line2: ILine,
  arcPoint: IPoint,
  textPosition: IPoint = arcPoint,
  minRadius: number = 1e-3
): IAngularDimensionData | null {
  const intersection = getLineIntersection(line1, line2)
  if (!intersection) {
    return null
  }

  const direction1 = getLineDirection(line1)
  const direction2 = getLineDirection(line2)
  if (!direction1 || !direction2) {
    return null
  }

  const sectorVector = new Vector3(arcPoint.x - intersection.x, arcPoint.y - intersection.y, 0)
  if (sectorVector.lengthSq() < EPSILON) {
    return null
  }

  const ray1 = direction1.dot(sectorVector) >= 0 ? direction1.clone() : direction1.clone().negate()
  const ray2 = direction2.dot(sectorVector) >= 0 ? direction2.clone() : direction2.clone().negate()
  if (Math.abs(ray1.x * ray2.y - ray1.y * ray2.x) < EPSILON) {
    return null
  }

  const angle1 = normalizeAngle(Math.atan2(ray1.y, ray1.x))
  const angle2 = normalizeAngle(Math.atan2(ray2.y, ray2.x))
  const sectorAngle = normalizeAngle(Math.atan2(sectorVector.y, sectorVector.x))

  const delta12 = getCcwDelta(angle1, angle2)
  const delta21 = getCcwDelta(angle2, angle1)

  let startAngle = angle1
  let sweepAngle = delta12

  const use12 = delta12 <= Math.PI + EPSILON && isAngleInCcwSweep(sectorAngle, angle1, delta12)
  const use21 = delta21 <= Math.PI + EPSILON && isAngleInCcwSweep(sectorAngle, angle2, delta21)

  if (use21 && (!use12 || delta21 < delta12)) {
    startAngle = angle2
    sweepAngle = delta21
  } else if (!use12 && !use21) {
    if (delta21 < delta12) {
      startAngle = angle2
      sweepAngle = delta21
    }
  }

  const endAngle = startAngle + sweepAngle
  const startDirection = angleToDirection(startAngle)
  const endDirection = angleToDirection(endAngle)
  const radius = Math.max(
    intersection.distanceTo(new Vector3(arcPoint.x, arcPoint.y, 0)),
    minRadius
  )
  const arcStart = intersection.clone().add(startDirection.clone().multiplyScalar(radius))
  const arcEnd = intersection.clone().add(endDirection.clone().multiplyScalar(radius))

  return {
    intersection,
    startDirection,
    endDirection,
    arcStart,
    arcEnd,
    radius,
    startAngle,
    endAngle,
    clockwise: false,
    sweepAngle,
    arcLength: radius * sweepAngle,
    textPosition: new Vector3(textPosition.x, textPosition.y, 0),
    angleDegrees: (sweepAngle * 180) / Math.PI,
  }
}

function getLineIntersection(line1: ILine, line2: ILine) {
  const x1 = line1.startPoint.x
  const y1 = line1.startPoint.y
  const x2 = line1.endPoint.x
  const y2 = line1.endPoint.y
  const x3 = line2.startPoint.x
  const y3 = line2.startPoint.y
  const x4 = line2.endPoint.x
  const y4 = line2.endPoint.y

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)
  if (Math.abs(denominator) < EPSILON) {
    return null
  }

  const determinant1 = x1 * y2 - y1 * x2
  const determinant2 = x3 * y4 - y3 * x4
  const x = (determinant1 * (x3 - x4) - (x1 - x2) * determinant2) / denominator
  const y = (determinant1 * (y3 - y4) - (y1 - y2) * determinant2) / denominator

  return new Vector3(x, y, 0)
}

function getLineDirection(line: ILine) {
  const direction = new Vector3(
    line.endPoint.x - line.startPoint.x,
    line.endPoint.y - line.startPoint.y,
    0
  )
  if (direction.lengthSq() < EPSILON) {
    return null
  }
  return direction.normalize()
}

function findAngularDimensionLineIndex(
  lines: readonly [ILine, ILine],
  direction: IPoint,
  excludedIndex?: number
) {
  let bestIndex = excludedIndex === 0 ? 1 : 0
  let bestScore = -Infinity

  lines.forEach((line, index) => {
    if (index === excludedIndex) {
      return
    }

    const dx = line.endPoint.x - line.startPoint.x
    const dy = line.endPoint.y - line.startPoint.y
    const length = Math.hypot(dx, dy)
    if (length <= EPSILON) {
      return
    }

    const score = Math.abs((dx / length) * direction.x + (dy / length) * direction.y)
    if (score > bestScore) {
      bestScore = score
      bestIndex = index
    }
  })

  return bestIndex
}

function getAngularDimensionLegPoint(
  line: ILine,
  intersection: IPoint,
  direction: IPoint,
  fallback: IPoint
): IPoint {
  const candidates = [line.startPoint, line.endPoint]
  let bestPoint = fallback
  let bestProjection = -Infinity

  for (const point of candidates) {
    const projection =
      (point.x - intersection.x) * direction.x + (point.y - intersection.y) * direction.y
    if (projection > bestProjection) {
      bestProjection = projection
      bestPoint = point
    }
  }

  return { x: bestPoint.x, y: bestPoint.y }
}

function getAngularDimensionVisibleLegLine(
  line: ILine,
  intersection: IPoint,
  direction: IPoint,
  arcPoint: IPoint
): ILine | null {
  const startProjection = getAngularDimensionRayProjection(line.startPoint, intersection, direction)
  const endProjection = getAngularDimensionRayProjection(line.endPoint, intersection, direction)
  const arcProjection = getAngularDimensionRayProjection(arcPoint, intersection, direction)
  const minProjection = Math.min(startProjection, endProjection)
  const maxProjection = Math.max(startProjection, endProjection)

  if (arcProjection >= minProjection - EPSILON && arcProjection <= maxProjection + EPSILON) {
    return null
  }

  let anchorPoint: IPoint
  if (arcProjection < minProjection) {
    anchorPoint = startProjection < endProjection ? line.startPoint : line.endPoint
  } else {
    anchorPoint = startProjection > endProjection ? line.startPoint : line.endPoint
  }
  if (Math.hypot(anchorPoint.x - arcPoint.x, anchorPoint.y - arcPoint.y) <= EPSILON) {
    return null
  }

  return {
    startPoint: toPoint(arcPoint),
    endPoint: toPoint(anchorPoint),
  }
}

function getAngularDimensionRayProjection(point: IPoint, intersection: IPoint, direction: IPoint) {
  return (point.x - intersection.x) * direction.x + (point.y - intersection.y) * direction.y
}

function normalizeAngle(angle: number) {
  let normalized = angle % PI2
  if (normalized < 0) {
    normalized += PI2
  }
  return normalized
}

function getCcwDelta(startAngle: number, endAngle: number) {
  return normalizeAngle(endAngle - startAngle)
}

function isAngleInCcwSweep(angle: number, startAngle: number, sweepAngle: number) {
  return getCcwDelta(startAngle, angle) <= sweepAngle + EPSILON
}

function angleToDirection(angle: number) {
  return new Vector3(Math.cos(angle), Math.sin(angle), 0)
}

function toPoint(point: IPoint): IPoint {
  return { x: point.x, y: point.y }
}
